import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileText, Download, Upload, AlertCircle, Eye, HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Document {
  type: string;
  label: string;
  required: boolean;
  count?: number;
  description: string;
  formats: string[];
  maxSize: number;
  tooltip?: string;
}

interface DocumentListProps {
  title: string;
  description: string;
  documents: Document[];
}

const DocumentList = ({ title, description, documents }: DocumentListProps) => {
  // Mock data for uploaded documents
  const mockUploads = {
    recommendation: [
      { name: "Recommendation_1.pdf", date: "2025-01-15", status: "uploaded" },
      { name: "Recommendation_2.pdf", date: "2025-01-16", status: "uploaded" }
    ],
    statement: [
      { name: "Personal_Statement.pdf", date: "2025-01-17", status: "uploaded" }
    ]
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "uploaded":
        return <Badge className="bg-green-500/10 text-green-500">Uploaded</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500/10 text-yellow-500">Pending</Badge>;
      default:
        return <Badge variant="secondary">Not Uploaded</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document</TableHead>
              <TableHead>Requirements</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.type}>
                <TableCell>
                  <div className="flex items-start gap-2">
                    <FileText className="h-5 w-5 mt-0.5 text-muted-foreground" />
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {doc.label}
                        {doc.required && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertCircle className="h-4 w-4 text-red-500" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Required document</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {doc.tooltip && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{doc.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {doc.description}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-muted-foreground">
                    <div>Format: {doc.formats.join(", ")}</div>
                    <div>Max size: {doc.maxSize}MB</div>
                    {doc.count && <div>Required: {doc.count} file(s)</div>}
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(mockUploads[doc.type]?.[0]?.status || "not_uploaded")}
                </TableCell>
                <TableCell>
                  {mockUploads[doc.type]?.[0]?.date
                    ? new Date(mockUploads[doc.type][0].date).toLocaleDateString()
                    : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {mockUploads[doc.type]?.[0]?.status === "uploaded" ? (
                      <>
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        <Button variant="outline" size="sm">
                          <Upload className="h-4 w-4 mr-1" />
                          Replace
                        </Button>
                      </>
                    ) : (
                      <Button size="sm">
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default DocumentList;